"use client";

import { motion } from "framer-motion";
import { useState, useMemo } from "react";
import { useRouter } from "next/navigation";
import { fadeInUp, staggerContainer } from "@/lib/animations";
import GlassCard from "@/components/ui/GlassCard";
import Button from "@/components/ui/Button";
import {
  Check,
  Clock,
  GraduationCap,
  Mail,
  Phone,
  Sparkles,
  Loader2,
  X,
} from "lucide-react";
import { useAPI } from "@/app/hooks";
import subscriptionService, {
  SubscriptionPlan,
} from "@/app/services/subscriptionService";

// Helper function to get icon based on room name
const getRoomIcon = (name: string): string => {
  const lowerName = name.toLowerCase();
  if (lowerName.includes("pod")) return "🔒";
  if (lowerName.includes("phone") || lowerName.includes("booth")) return "📞";
  if (lowerName.includes("meeting") || lowerName.includes("room")) return "🏢";
  return "🏢"; // default icon
};

// Student Discount Banner Component
const StudentDiscountBanner = () => (
  <motion.div variants={fadeInUp} className="mb-8 sm:mb-12">
    <GlassCard className="relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-r from-primary/10 via-purple-500/5 to-primary/10 opacity-50" />

      <div className="relative p-6 sm:p-8">
        <div className="flex flex-col md:flex-row items-center gap-6">
          <div className="flex-shrink-0">
            <div className="relative">
              <div className="absolute inset-0 bg-primary/20 blur-xl rounded-full animate-pulse" />
              <div className="relative w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-gradient-to-br from-primary to-purple-600 flex items-center justify-center shadow-lg">
                <GraduationCap className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
              </div>
            </div>
          </div>

          <div className="flex-1 text-center md:text-left">
            <div className="flex items-center justify-center md:justify-start gap-2 mb-2">
              <Sparkles className="w-5 h-5 text-primary" />
              <h3 className="text-xl sm:text-2xl font-bold text-heading">
                Special Student Discount
              </h3>
            </div>
            <p className="text-text text-sm sm:text-base mb-3 leading-relaxed">
              A dedicated discount is available for university students.
            </p>
            <div className="flex items-start justify-center md:justify-start gap-2 text-text text-xs sm:text-sm">
              <Mail className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
              <p>
                Send your{" "}
                <span className="font-semibold text-heading">
                  University Name
                </span>{" "}
                and{" "}
                <span className="font-semibold text-heading">Student ID</span>{" "}
                to{" "}
                <a
                  href="mailto:office@idworkglobal.com"
                  className="text-primary hover:text-primary/80 font-semibold underline decoration-primary/30 hover:decoration-primary/60 transition-all"
                >
                  office@idworkglobal.com
                </a>{" "}
                for verification.
              </p>
            </div>
          </div>

          <div className="flex-shrink-0">
            <a href="mailto:office@idworkglobal.com?subject=Student%20Discount%20Request">
              <Button variant="primary" size="lg" className="whitespace-nowrap">
                Apply Now
              </Button>
            </a>
          </div>
        </div>
      </div>
    </GlassCard>
  </motion.div>
);

// Contact message shown on membership cards when show_price is false
const MembershipContactInfo = () => (
  <div className="mt-4 p-4 rounded-lg bg-primary/5 border border-primary/20 text-left">
    <p className="text-heading text-sm sm:text-base font-semibold mb-3">
      Contact us now to get started with your subscription:
    </p>
    <ul className="space-y-2 text-text text-sm">
      <li className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <Mail className="w-4 h-4 text-primary flex-shrink-0" />
        <span>Email:</span>
        <a
          href="mailto:mohammad.hawamdah@idworkglobal.com"
          className="text-primary font-medium hover:underline break-all"
        >
          mohammad.hawamdah@idworkglobal.com
        </a>
      </li>
      <li className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <Phone className="w-4 h-4 text-primary flex-shrink-0" />
        <span>Call / WhatsApp:</span>
        <a
          href="tel:+966554281188"
          dir="ltr"
          className="text-primary font-medium hover:underline"
        >
          +966 55 428 1188
        </a>
      </li>
    </ul>
  </div>
);

// Membership Card Component
const MembershipCard = ({
  plan,
  index,
  isPopular,
}: {
  plan: SubscriptionPlan;
  index: number;
  isPopular: boolean;
}) => {
  const router = useRouter();

  const handleChoosePlan = () => {
    const effectivePrice = subscriptionService.getEffectivePrice(plan);
    const duration = subscriptionService.getDurationDisplay(plan.duration_days);

    const params = new URLSearchParams({
      plan: plan.name,
      price: effectivePrice.toString(),
      planId: plan.id,
      duration: duration,
      duration_days: plan.duration_days.toString(),
    });

    router.push(`/payment?${params.toString()}`);
  };

  // show_price true  -> original card: price + payment
  // show_price false -> price hidden + contact message, no payment
  const showPrice = plan.show_price;

  const effectivePrice = subscriptionService.getEffectivePrice(plan);
  const originalPrice = subscriptionService.parsePrice(plan.price);
  const hasDiscount = plan.has_offer && plan.offer_price;
  const duration = subscriptionService.getDurationDisplay(plan.duration_days);

  return (
    <GlassCard
      key={plan.id}
      className={`p-6 sm:p-8 relative ${
        isPopular ? "ring-2 ring-primary shadow-xl" : ""
      }`}
    >
      {isPopular && (
        <div className="absolute -top-4 sm:-top-5 left-1/2 -translate-x-1/2 z-10">
          <span className="bg-primary text-white px-4 sm:px-6 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-semibold shadow-lg whitespace-nowrap">
            Best Value
          </span>
        </div>
      )}

      <div className="text-center mb-6 sm:mb-8">
        <h3 className="text-heading text-xl sm:text-2xl font-bold mb-2">
          {plan.name}
        </h3>
        <p className="text-text text-sm sm:text-base mb-4">
          {plan.description}
        </p>

        {hasDiscount && plan.offer_name && (
          <div className="mb-3">
            <span className="inline-block bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 px-3 py-1 rounded-full text-xs sm:text-sm font-semibold">
              {plan.offer_name}
            </span>
          </div>
        )}

        {/* Price: only when show_price is true */}
        {showPrice && (
          <div className="mb-2">
            <motion.span
              key={effectivePrice}
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-4xl sm:text-5xl font-bold text-heading"
            >
              {effectivePrice}
            </motion.span>
            <span className="text-text text-base sm:text-lg ml-2">SAR</span>

            {hasDiscount && (
              <div className="mt-1">
                <span className="text-text/50 line-through text-sm">
                  {originalPrice} SAR
                </span>
                {plan.discount_percentage && (
                  <span className="ml-2 text-green-600 dark:text-green-400 text-sm font-semibold">
                    Save {plan.discount_percentage}%
                  </span>
                )}
              </div>
            )}
          </div>
        )}

        <p className="text-text text-xs sm:text-sm">{duration}</p>

        {/* Contact message: only when show_price is false */}
        {!showPrice && <MembershipContactInfo />}
      </div>

      <ul className="space-y-3 sm:space-y-4 mb-6 sm:mb-8">
        {plan.features.map((feature: string, idx: number) => (
          <li key={idx} className="flex items-start gap-3">
            <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
              <Check className="w-3 h-3 text-primary" />
            </div>
            <span className="text-text text-sm sm:text-base leading-relaxed">
              {feature}
            </span>
          </li>
        ))}
      </ul>

      {/* Payment: only when show_price is true */}
      {showPrice && (
        <Button
          variant={isPopular ? "primary" : "outline"}
          className="w-full"
          size="lg"
          onClick={handleChoosePlan}
        >
          Choose Plan
        </Button>
      )}
    </GlassCard>
  );
};

// Room Rental Card Component
const RoomRentalCard = ({
  room,
  index,
}: {
  room: SubscriptionPlan;
  index: number;
}) => {
  const router = useRouter();

  const handleBookRoom = () => {
    const effectivePrice = subscriptionService.getEffectivePrice(room);

    const params = new URLSearchParams({
      plan: room.name,
      price: effectivePrice.toString(),
      planId: room.id,
      duration: "per day", // Fixed to "per day" for room rentals
      duration_days: room.duration_days.toString(),
    });

    router.push(`/payment?${params.toString()}`);
  };

  const effectivePrice = subscriptionService.getEffectivePrice(room);
  const originalPrice = subscriptionService.parsePrice(room.price);
  const hasDiscount = room.has_offer && room.offer_price;
  const icon = getRoomIcon(room.name);

  return (
    <GlassCard key={room.id} className="p-6 sm:p-8 relative">
      <div className="text-center mb-6">
        <div className="text-5xl mb-4">{icon}</div>
        <h3 className="text-heading text-xl sm:text-2xl font-bold mb-2">
          {room.name}
        </h3>
        <p className="text-text text-sm sm:text-base mb-6">
          {room.description}
        </p>

        {hasDiscount && room.offer_name && (
          <div className="mb-3">
            <span className="inline-block bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 px-3 py-1 rounded-full text-xs sm:text-sm font-semibold">
              {room.offer_name}
            </span>
          </div>
        )}

        <div className="mb-2">
          <motion.span className="text-4xl sm:text-5xl font-bold text-heading">
            {effectivePrice}
          </motion.span>
          <span className="text-text text-base sm:text-lg ml-2">SAR</span>

          {hasDiscount && (
            <div className="mt-1">
              <span className="text-text/50 line-through text-sm">
                {originalPrice} SAR
              </span>
              {room.discount_percentage && (
                <span className="ml-2 text-green-600 dark:text-green-400 text-sm font-semibold">
                  Save {room.discount_percentage}%
                </span>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-center gap-2 text-text text-xs sm:text-sm">
          <Clock className="w-4 h-4" />
          <span>per day</span>
        </div>
      </div>

      <ul className="space-y-3 sm:space-y-4 mb-6 sm:mb-8">
        {room.features.map((feature: string, idx: number) => (
          <li key={idx} className="flex items-start gap-3">
            <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
              <Check className="w-3 h-3 text-primary" />
            </div>
            <span className="text-text text-sm sm:text-base leading-relaxed">
              {feature}
            </span>
          </li>
        ))}
      </ul>

      <Button
        variant="outline"
        className="w-full"
        size="lg"
        onClick={handleBookRoom}
      >
        Book Now
      </Button>
    </GlassCard>
  );
};

// Main Component
export default function MembershipPlans() {
  const [activeTab, setActiveTab] = useState<"memberships" | "rooms">(
    "memberships",
  );

  // Fetch membership plans from API
  const {
    data: membershipResponse,
    loading: membershipLoading,
    error: membershipError,
    refetch: refetchMemberships,
  } = useAPI(
    () =>
      subscriptionService.getMembershipPlans({
        pageNumber: 1,
        limit: 100,
        is_active: true,
      }),
    [],
  );

  // Fetch room rental plans from API
  const {
    data: roomRentalResponse,
    loading: roomRentalLoading,
    error: roomRentalError,
    refetch: refetchRoomRentals,
  } = useAPI(
    () =>
      subscriptionService.getRoomRentalPlans({
        pageNumber: 1,
        limit: 100,
        is_active: true,
      }),
    [],
  );

  // Sort membership plans by price (low to high) and mark 3 Months as best value
  const { sortedPlans, bestValueId } = useMemo(() => {
    const plans = membershipResponse?.rows || [];

    // Sort by effective price
    const sorted = [...plans].sort((a, b) => {
      const priceA = subscriptionService.getEffectivePrice(a);
      const priceB = subscriptionService.getEffectivePrice(b);
      return priceA - priceB;
    });

    // Find the "3 Months" plan and mark it as best value
    const threeMonthPlan = sorted.find((plan) => plan.name === "3 Months");
    const bestValue = threeMonthPlan?.id || null;

    return { sortedPlans: sorted, bestValueId: bestValue };
  }, [membershipResponse]);

  // Sort room rentals by price (low to high)
  const sortedRoomRentals = useMemo(() => {
    const rooms = roomRentalResponse?.rows || [];

    // Sort by effective price
    return [...rooms].sort((a, b) => {
      const priceA = subscriptionService.getEffectivePrice(a);
      const priceB = subscriptionService.getEffectivePrice(b);
      return priceA - priceB;
    });
  }, [roomRentalResponse]);

  return (
    <section
      id="membership"
      className="py-16 sm:py-24 lg:py-32 px-4 sm:px-6 lg:px-8"
    >
      <div className="container mx-auto max-w-7xl">
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
        >
          {/* Section Header */}
          <motion.div
            variants={fadeInUp}
            className="text-center mb-12 sm:mb-16"
          >
            <h2 className="text-heading text-3xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6 px-4">
              Membership & Spaces
            </h2>
            <p className="text-text text-base sm:text-lg md:text-xl max-w-3xl mx-auto mb-6 sm:mb-8 px-4 leading-relaxed">
              Choose from flexible memberships or book private spaces by the
              hour
            </p>

            {/* Tab Toggle */}
            <div className="inline-flex flex-col sm:flex-row items-center gap-2 sm:gap-1 p-1.5 sm:p-2 rounded-xl sm:rounded-2xl bg-card/80 backdrop-blur-xl border border-border shadow-sm">
              <button
                onClick={() => setActiveTab("memberships")}
                className={`w-full sm:w-auto px-6 sm:px-8 py-2.5 sm:py-2 rounded-lg sm:rounded-xl font-semibold text-sm sm:text-base transition-all duration-300 ${
                  activeTab === "memberships"
                    ? "bg-primary text-white shadow-lg"
                    : "text-text hover:text-heading hover:bg-card/50"
                }`}
              >
                Memberships
              </button>
              <button
                onClick={() => setActiveTab("rooms")}
                className={`w-full sm:w-auto px-6 sm:px-8 py-2.5 sm:py-2 rounded-lg sm:rounded-xl font-semibold text-sm sm:text-base transition-all duration-300 ${
                  activeTab === "rooms"
                    ? "bg-primary text-white shadow-lg"
                    : "text-text hover:text-heading hover:bg-card/50"
                }`}
              >
                Room Rentals
              </button>
            </div>
          </motion.div>

          {/* Student Discount Banner */}
          <StudentDiscountBanner />

          {/* Memberships Section */}
          {activeTab === "memberships" && (
            <>
              {/* Loading State */}
              {membershipLoading && (
                <div className="flex items-center justify-center py-20">
                  <Loader2 className="w-10 h-10 text-primary animate-spin" />
                </div>
              )}

              {/* Error State */}
              {membershipError && (
                <div className="text-center py-12">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center">
                    <X className="w-8 h-8 text-red-600" />
                  </div>
                  <p className="text-red-600 mb-4">{membershipError}</p>
                  <button
                    onClick={refetchMemberships}
                    className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                  >
                    Try Again
                  </button>
                </div>
              )}

              {/* Plans Grid */}
              {!membershipLoading &&
                !membershipError &&
                sortedPlans.length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
                    {sortedPlans.map((plan, index) => (
                      <MembershipCard
                        key={plan.id}
                        plan={plan}
                        index={index}
                        isPopular={plan.id === bestValueId}
                      />
                    ))}
                  </div>
                )}

              {/* Empty State */}
              {!membershipLoading &&
                !membershipError &&
                sortedPlans.length === 0 && (
                  <div className="text-center py-12">
                    <p className="text-text text-lg">
                      No membership plans available at the moment.
                    </p>
                  </div>
                )}
            </>
          )}

          {/* Room Rentals Section */}
          {activeTab === "rooms" && (
            <>
              {/* Loading State */}
              {roomRentalLoading && (
                <div className="flex items-center justify-center py-20">
                  <Loader2 className="w-10 h-10 text-primary animate-spin" />
                </div>
              )}

              {/* Error State */}
              {roomRentalError && (
                <div className="text-center py-12">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center">
                    <X className="w-8 h-8 text-red-600" />
                  </div>
                  <p className="text-red-600 mb-4">{roomRentalError}</p>
                  <button
                    onClick={refetchRoomRentals}
                    className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                  >
                    Try Again
                  </button>
                </div>
              )}

              {/* Room Rentals Grid */}
              {!roomRentalLoading &&
                !roomRentalError &&
                sortedRoomRentals.length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
                    {sortedRoomRentals.map((room, index) => (
                      <RoomRentalCard key={room.id} room={room} index={index} />
                    ))}
                  </div>
                )}

              {/* Empty State */}
              {!roomRentalLoading &&
                !roomRentalError &&
                sortedRoomRentals.length === 0 && (
                  <div className="text-center py-12">
                    <p className="text-text text-lg">
                      No room rentals available at the moment.
                    </p>
                  </div>
                )}
            </>
          )}
        </motion.div>
      </div>
    </section>
  );
}
